"use client";

import React, { useEffect, useRef, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.15,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const categories = [{
  id: "cat-1",
  title: "Informações Pessoais"
}, {
  id: "cat-2",
  title: "Dados de Navegação e Dispositivo"
}, {
  id: "cat-3",
  title: "Compartilhamento de Informações"
}, {
  id: "cat-4",
  title: "Seus Direitos e Controle"
}];
const details = [{
  categoryId: "cat-1",
  text: "Coletamos dados fornecidos voluntariamente, como nome, endereço de e-mail e número de telefone, através de formulários de contato e processos de registro."
}, {
  categoryId: "cat-1",
  text: "Essas informações são utilizadas exclusivamente para comunicação direta, suporte ao cliente e prestação dos serviços solicitados por você."
}, {
  categoryId: "cat-1",
  text: "Garantimos a segurança dos seus dados e não os comercializamos, alugamos ou trocamos com terceiros sob nenhuma circunstância."
}, {
  categoryId: "cat-2",
  text: "Utilizamos cookies e tecnologias de rastreamento semelhantes para entender como você interage com nossa plataforma e melhorar sua experiência."
}, {
  categoryId: "cat-2",
  text: "Coletamos automaticamente informações técnicas, incluindo seu endereço IP, tipo de navegador, páginas visitadas e tempo de permanência no site."
}, {
  categoryId: "cat-2",
  text: "O objetivo principal desta coleta é aprimorar continuamente a usabilidade do site, diagnosticar problemas técnicos e otimizar nosso conteúdo."
}, {
  categoryId: "cat-3",
  text: "Compartilhamos dados apenas com parceiros de serviço essenciais (como provedores de hospedagem) necessários para a operação técnica da plataforma."
}, {
  categoryId: "cat-3",
  text: "Todos os nossos parceiros estão sujeitos a rigorosos acordos de confidencialidade e padrões de segurança da informação."
}, {
  categoryId: "cat-3",
  text: "Podemos divulgar informações às autoridades legais competentes apenas quando estritamente exigido por lei ou ordem judicial."
}, {
  categoryId: "cat-4",
  text: "Você tem o direito fundamental de solicitar uma cópia de todos os seus dados pessoais atualmente armazenados em nossos sistemas."
}, {
  categoryId: "cat-4",
  text: "É possível solicitar a correção imediata de quaisquer dados incompletos, inexatos ou desatualizados a qualquer momento."
}, {
  categoryId: "cat-4",
  text: "Você pode requerer a exclusão total dos seus dados de nossos bancos de dados, respeitando apenas os prazos legais obrigatórios de guarda."
}];
export default function ColetaEUsoDeDados() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="coleta-e-uso-de-dados" className="bg-background w-full">
      <div className="bg-muted w-full py-16 md:py-24">
        <div ref={ref} className={`container mx-auto px-4 md:px-6 max-w-4xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground tracking-tight">
              Coleta e Uso de Dados
            </h2>
            <p className="mt-4 text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
              Transparência é fundamental. Entenda detalhadamente como
              processamos, protegemos e utilizamos as informações que você
              compartilha conosco.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4">
            {categories.map((category, index) => {
            const categoryDetails = details.filter(d => d.categoryId === category.id);
            return <AccordionItem key={category.id} value={category.id} data-index={index} className="bg-card border border-border/40 rounded-[16px] shadow-sm transition-all duration-200 ease-in-out hover:-translate-y-[2px] hover:shadow-md data-[state=open]:shadow-md overflow-hidden">
                  <AccordionTrigger className="px-5 py-5 md:px-6 md:py-6 hover:no-underline group">
                    <h3 className="font-default text-lg md:text-xl font-semibold text-primary text-left transition-colors group-hover:text-primary/80">
                      {category.title}
                    </h3>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-5 px-5 pb-6 pt-2 md:px-6 md:pb-8">
                      {categoryDetails.map((detail, detailIndex) => <li key={detailIndex} data-index={detailIndex} className="flex items-start gap-4 md:gap-5">
                          <span className="flex items-center justify-center w-6 h-6 md:w-7 md:h-7 rounded-full bg-secondary text-secondary-foreground text-xs md:text-sm font-bold shrink-0 mt-0.5 shadow-sm">
                            {detailIndex + 1}
                          </span>
                          <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                            {detail.text}
                          </p>
                        </li>)}
                    </ul>
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>
        </div>
      </div>
    </section>;
}